import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight, CalendarDays, Phone, BookOpen, ExternalLink, Search } from "lucide-react";
import { useSEO } from "@/hooks/useSEO";
import { PageHeader } from "@/components/PageHeader";
import { Reveal } from "@/components/Reveal";
import { ConsultationCTA } from "@/components/ConsultationCTA";
import { api } from "@/lib/api";
import { SERVICES } from "@/data/services";
import { AREAS } from "@/data/areas";
import { COMPANY } from "@/lib/constants";

const fmtDate = (iso) => new Date(iso).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" });
const readTime = (content = "") => `${Math.max(2, Math.round(content.split(/\s+/).length / 200))} min read`;

const POPULAR_SEARCHES = [
  "home health care Northern Virginia",
  "skilled nursing at home Annandale",
  "in-home physical therapy Fairfax",
  "private duty caregivers Arlington",
  "personal care aide Alexandria",
  "post-hospital home care Virginia",
  "Medicare home health Northern VA",
  "speech therapy at home Fairfax County",
];

const TRUSTED_LINKS = [
  { label: "Virginia Department of Health", desc: "Statewide public health resources", url: "https://www.vdh.virginia.gov/" },
  { label: "Medicare Home Health", desc: "Coverage info for home health care", url: "https://www.medicare.gov/coverage/home-health-services" },
  { label: "National Institute on Aging", desc: "Trusted guidance on aging & care", url: "https://www.nia.nih.gov/" },
  { label: "Virginia Association for Home Care", desc: "Home health & hospice partners", url: "https://www.vahc.org/" },
  { label: "Eldercare Locator", desc: "Find senior services nationwide", url: "https://eldercare.acl.gov/" },
  { label: "Alzheimer's Association", desc: "Dementia caregiving resources", url: "https://www.alz.org/" },
];

export default function Resources() {
  useSEO({
    title: "Home Care Resources & Guides | Esprit Home Healthcare Northern Virginia",
    description: "Helpful guides, articles, and trusted resources for Northern Virginia families navigating home health care, recovery, insurance, and senior wellness.",
    canonical: "/resources",
  });

  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("All");

  useEffect(() => {
    api.get("/blog").then((r) => setPosts(r.data)).finally(() => setLoading(false));
  }, []);

  const tags = ["All", ...Array.from(new Set(posts.flatMap((p) => p.tags || [])))];
  const filtered = filter === "All" ? posts : posts.filter((p) => (p.tags || []).includes(filter));
  const featured = posts[0];
  const rest = (filter === "All" ? filtered.slice(1) : filtered);

  return (
    <div>
      <PageHeader
        overline="Resources & Insights"
        title="Home Care, Understood."
        subtitle="Guidance for families making care decisions — how to recognize when a loved one needs support, what to expect with recovery and costs, and trusted resources to move forward with confidence."
      />

      <section className="container-page py-16 lg:py-20">
        {/* Featured */}
        {featured && filter === "All" && (
          <Reveal>
            <Link to={`/blog/${featured.slug}`} data-testid="featured-article"
              className="group grid lg:grid-cols-2 gap-8 items-center bg-card rounded-[2rem] border border-border overflow-hidden mb-14 hover:shadow-lg transition-shadow">
              {featured.cover_image && (
                <div className="h-64 lg:h-full overflow-hidden">
                  <img src={featured.cover_image} alt={featured.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
              )}
              <div className="p-8">
                <span className="inline-flex items-center gap-2 rounded-full bg-accent/15 text-accent px-3 py-1 text-sm font-semibold">
                  <BookOpen className="w-4 h-4" /> Featured
                </span>
                <h2 className="font-heading font-extrabold text-3xl text-primary mt-3 leading-tight">{featured.title}</h2>
                <p className="text-muted-foreground mt-3">{featured.excerpt}</p>
                <span className="inline-flex items-center gap-1 text-primary font-semibold mt-5 group-hover:gap-2 transition-all">
                  Read the article <ArrowRight className="w-4 h-4" />
                </span>
              </div>
            </Link>
          </Reveal>
        )}

        {/* Filter tabs */}
        {tags.length > 1 && (
          <div className="flex flex-wrap gap-2 mb-8">
            {tags.map((t) => (
              <button key={t} onClick={() => setFilter(t)} data-testid={`filter-${t}`}
                className={`rounded-full px-5 h-11 font-semibold transition-colors ${filter === t ? "bg-primary text-primary-foreground" : "bg-muted text-foreground/80 hover:bg-border"}`}>
                {t}
              </button>
            ))}
          </div>
        )}

        {/* Articles grid */}
        {loading ? (
          <p className="text-muted-foreground text-lg">Loading articles…</p>
        ) : rest.length === 0 ? (
          <p className="text-muted-foreground text-lg">No articles in this category yet.</p>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {rest.map((p, i) => (
              <Reveal key={p.id} delay={i * 0.06}>
                <Link to={`/blog/${p.slug}`} data-testid={`article-card-${i}`}
                  className="group flex flex-col h-full bg-card rounded-2xl border border-border overflow-hidden hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                  {p.cover_image && (
                    <div className="overflow-hidden h-48">
                      <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                    </div>
                  )}
                  <div className="p-6 flex flex-col flex-1">
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      {p.tags?.[0] && <span className="text-accent font-semibold">{p.tags[0]}</span>}
                      <span>·</span>
                      <span>{readTime(p.content)}</span>
                    </div>
                    <h3 className="font-heading font-bold text-xl text-primary mt-2 leading-snug">{p.title}</h3>
                    <p className="text-muted-foreground mt-2 flex-1 text-sm">{p.excerpt}</p>
                    <span className="inline-flex items-center gap-1 text-primary font-semibold mt-4 group-hover:gap-2 transition-all">
                      Read article <ArrowRight className="w-4 h-4" />
                    </span>
                  </div>
                </Link>
              </Reveal>
            ))}
          </div>
        )}
      </section>

      {/* SEO hub: searches + services + areas + trusted links */}
      <section className="bg-[hsl(var(--muted))]">
        <div className="container-page py-16 lg:py-20 space-y-12">
          <Reveal>
            <h2 className="font-heading font-bold text-2xl text-primary flex items-center gap-2"><Search className="w-6 h-6" /> Popular Searches We Help With</h2>
            <div className="flex flex-wrap gap-2 mt-5">
              {POPULAR_SEARCHES.map((s) => (
                <Link key={s} to="/contact" className="rounded-full bg-card border border-border hover:border-primary px-4 py-2 text-sm transition-colors capitalize">{s}</Link>
              ))}
            </div>
          </Reveal>

          <div className="grid md:grid-cols-2 gap-10">
            <Reveal>
              <h3 className="font-heading font-bold text-xl text-primary mb-4">Browse Our Services</h3>
              <ul className="grid sm:grid-cols-2 gap-2">
                {SERVICES.map((s) => (
                  <li key={s.slug}>
                    <Link to={`/services/${s.slug}`} className="flex items-center gap-2 text-foreground/90 hover:text-primary group py-1">
                      <ArrowRight className="w-4 h-4 text-accent group-hover:translate-x-1 transition-transform" /> {s.shortName}
                    </Link>
                  </li>
                ))}
              </ul>
            </Reveal>
            <Reveal delay={0.1}>
              <h3 className="font-heading font-bold text-xl text-primary mb-4">Areas We Serve</h3>
              <div className="flex flex-wrap gap-2">
                {AREAS.map((a) => (
                  <Link key={a.slug} to={`/service-areas/${a.slug}`} className="rounded-full bg-card border border-border hover:bg-primary hover:text-primary-foreground px-3 py-1.5 text-sm transition-colors">{a.name}</Link>
                ))}
              </div>
            </Reveal>
          </div>

          <Reveal>
            <h3 className="font-heading font-bold text-xl text-primary mb-4">Trusted Resources for Families</h3>
            <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {TRUSTED_LINKS.map((l) => (
                <a key={l.url} href={l.url} target="_blank" rel="noreferrer noopener"
                  className="group flex items-start gap-3 bg-card rounded-2xl border border-border p-5 hover:border-primary transition-colors">
                  <ExternalLink className="w-5 h-5 text-primary shrink-0 mt-0.5" />
                  <span>
                    <span className="font-semibold text-foreground block group-hover:text-primary">{l.label}</span>
                    <span className="text-sm text-muted-foreground">{l.desc}</span>
                  </span>
                </a>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* Still have questions */}
      <section className="container-page py-16 text-center">
        <Reveal>
          <h2 className="font-heading font-bold text-3xl md:text-4xl text-primary max-w-2xl mx-auto">Still have questions?</h2>
          <p className="text-lg text-muted-foreground mt-3 max-w-xl mx-auto">
            If you'd rather talk to a person than read another article, we understand. A free consultation with our care team is often the fastest way to clarity.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4 mt-7">
            <Link to="/contact" className="inline-flex items-center justify-center gap-2 rounded-full bg-accent hover:bg-accent/90 text-accent-foreground h-14 px-7 text-lg font-bold transition-colors">
              Book a Free Consultation <ArrowRight className="w-5 h-5" />
            </Link>
            <a href={COMPANY.phoneHref} className="inline-flex items-center justify-center gap-2 rounded-full border-2 border-primary text-primary hover:bg-primary hover:text-primary-foreground h-14 px-7 text-lg font-bold transition-colors">
              <Phone className="w-5 h-5" /> Call {COMPANY.phone}
            </a>
          </div>
        </Reveal>
      </section>

      <ConsultationCTA />
    </div>
  );
}
